import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sheetsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, Bold, Italic, AlignLeft, AlignCenter, AlignRight,
  Users, Underline, Strikethrough, Download, Upload, Undo, Redo, Copy, Scissors,
  ClipboardPaste, Search, Filter, SortAsc, SortDesc, Merge, LayoutDashboard,
  PaintBucket, Type, Columns, Rows, ChevronDown
} from 'lucide-react';

const COLS = 26;
const ROWS = 100;
const COL_LETTERS = Array.from({ length: COLS }, (_, i) => String.fromCharCode(65 + i));
const DEFAULT_COL_W = 100;
const MIN_COL_W = 40;
const DEFAULT_ROW_H = 28;
const FONT_SIZES = ['8','9','10','11','12','14','16','18','20','24','28','32','36'];
const CELL_COLORS = ['transparent','#fff2cc','#d9ead3','#cfe2f3','#ead1dc','#fce5cd','#d0e0e3','#f4cccc','#e6b8af'];
const TEXT_COLORS = ['#000000','#434343','#666666','#999999','#e06666','#f6b26b','#ffd966','#93c47d','#76a5af','#6fa8dc','#8e7cc3','#c27ba0'];

// ========= FORMULA ENGINE =========
function evalFormula(formula, getData) {
  if (!formula || typeof formula !== 'string' || !formula.startsWith('=')) return formula;
  const expr = formula.substring(1).toUpperCase().trim();
  try {
    // Range reference parser
    const parseRange = (c1, r1, c2, r2) => {
      const ci1 = c1.charCodeAt(0) - 65, ci2 = c2.charCodeAt(0) - 65;
      const ri1 = parseInt(r1) - 1, ri2 = parseInt(r2) - 1;
      const vals = [];
      for (let r = Math.min(ri1, ri2); r <= Math.max(ri1, ri2); r++) {
        for (let c = Math.min(ci1, ci2); c <= Math.max(ci1, ci2); c++) {
          const v = parseFloat(getData(r, c));
          if (!isNaN(v)) vals.push(v);
        }
      }
      return vals;
    };

    // Function patterns
    const fnMatch = expr.match(/^(SUM|AVERAGE|AVG|MIN|MAX|COUNT|COUNTA|IF|ROUND|ABS|SQRT|POWER|CONCATENATE|LEN|UPPER|LOWER|TRIM|LEFT|RIGHT|MID|TODAY|NOW)\((.*)\)$/);
    if (fnMatch) {
      const [, fn, args] = fnMatch;
      // Range-based functions
      const rangeArgs = args.match(/^([A-Z])(\d+):([A-Z])(\d+)$/);
      if (rangeArgs) {
        const vals = parseRange(rangeArgs[1], rangeArgs[2], rangeArgs[3], rangeArgs[4]);
        if (fn === 'SUM') return vals.reduce((a, b) => a + b, 0);
        if (fn === 'AVERAGE' || fn === 'AVG') return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        if (fn === 'MIN') return vals.length ? Math.min(...vals) : 0;
        if (fn === 'MAX') return vals.length ? Math.max(...vals) : 0;
        if (fn === 'COUNT') return vals.length;
        if (fn === 'COUNTA') return vals.length;
      }
      // IF function
      if (fn === 'IF') {
        const parts = splitArgs(args);
        if (parts.length >= 3) {
          const cond = evalExpr(parts[0], getData);
          return cond ? evalExpr(parts[1], getData) : evalExpr(parts[2], getData);
        }
      }
      // Math functions
      if (fn === 'ABS') return Math.abs(evalExpr(args, getData));
      if (fn === 'SQRT') return Math.sqrt(evalExpr(args, getData));
      if (fn === 'ROUND') { const p = splitArgs(args); return Number(evalExpr(p[0], getData)).toFixed(parseInt(p[1]) || 0); }
      if (fn === 'POWER') { const p = splitArgs(args); return Math.pow(evalExpr(p[0], getData), evalExpr(p[1], getData)); }
      // String functions
      if (fn === 'LEN') return String(evalExpr(args, getData)).length;
      if (fn === 'UPPER') return String(evalExpr(args, getData)).toUpperCase();
      if (fn === 'LOWER') return String(evalExpr(args, getData)).toLowerCase();
      if (fn === 'TRIM') return String(evalExpr(args, getData)).trim();
      if (fn === 'CONCATENATE') return splitArgs(args).map(a => evalExpr(a, getData)).join('');
      if (fn === 'LEFT') { const p = splitArgs(args); return String(evalExpr(p[0], getData)).slice(0, parseInt(p[1]) || 1); }
      if (fn === 'RIGHT') { const p = splitArgs(args); return String(evalExpr(p[0], getData)).slice(-(parseInt(p[1]) || 1)); }
      if (fn === 'TODAY') return new Date().toISOString().split('T')[0];
      if (fn === 'NOW') return new Date().toLocaleString();
    }

    // Simple arithmetic with cell refs
    return evalExpr(expr, getData);
  } catch { return '#ERR'; }
}

function splitArgs(str) {
  const result = []; let depth = 0; let current = '';
  for (const ch of str) {
    if (ch === '(' ) depth++;
    if (ch === ')') depth--;
    if (ch === ',' && depth === 0) { result.push(current.trim()); current = ''; }
    else current += ch;
  }
  if (current) result.push(current.trim());
  return result;
}

function evalExpr(expr, getData) {
  if (typeof expr === 'number') return expr;
  expr = String(expr).trim();
  if (expr.startsWith('"') && expr.endsWith('"')) return expr.slice(1, -1);
  const num = parseFloat(expr);
  if (!isNaN(num) && String(num) === expr) return num;
  // Cell reference
  const cellRef = expr.match(/^([A-Z])(\d+)$/);
  if (cellRef) {
    const v = getData(parseInt(cellRef[2]) - 1, cellRef[1].charCodeAt(0) - 65);
    const n = parseFloat(v);
    return isNaN(n) ? v : n;
  }
  // Comparison operators
  for (const op of ['>=', '<=', '<>', '!=', '>', '<', '=']) {
    const idx = expr.indexOf(op);
    if (idx > 0) {
      const l = evalExpr(expr.slice(0, idx), getData);
      const r = evalExpr(expr.slice(idx + op.length), getData);
      if (op === '>') return l > r; if (op === '<') return l < r;
      if (op === '>=' ) return l >= r; if (op === '<=') return l <= r;
      if (op === '=' || op === '==') return l === r;
      if (op === '<>' || op === '!=') return l !== r;
    }
  }
  // Arithmetic
  const replaced = expr.replace(/([A-Z])(\d+)/g, (_, col, row) => {
    const v = getData(parseInt(row) - 1, col.charCodeAt(0) - 65);
    return isNaN(parseFloat(v)) ? '0' : v;
  });
  try { return Function('"use strict"; return (' + replaced + ')')(); } catch { return expr; }
}

// ========= CLIPBOARD & UNDO =========
function useUndoRedo(initial) {
  const [history, setHistory] = useState([initial]);
  const [idx, setIdx] = useState(0);
  const current = history[idx];
  const push = useCallback((val) => {
    setHistory(prev => [...prev.slice(0, idx + 1), val]);
    setIdx(prev => prev + 1);
  }, [idx]);
  const undo = useCallback(() => setIdx(prev => Math.max(0, prev - 1)), []);
  const redo = useCallback(() => setIdx(prev => Math.min(history.length - 1, prev + 1)), [history.length]);
  return { current, push, undo, redo, canUndo: idx > 0, canRedo: idx < history.length - 1 };
}

// ========= MAIN COMPONENT =========
export default function SheetEditor() {
  const { sheetId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [sheet, setSheet] = useState(null);
  const [title, setTitle] = useState('');
  const [activeSheet, setActiveSheet] = useState(0);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selRange, setSelRange] = useState(null); // {r1,c1,r2,c2}
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [colWidths, setColWidths] = useState({});
  const [clipboard, setClipboard] = useState(null);
  const [showFind, setShowFind] = useState(false);
  const [findText, setFindText] = useState('');
  const [fontSize, setFontSize] = useState('12');
  const [showCsvImport, setShowCsvImport] = useState(false);
  const saveTimerRef = useRef(null);
  const inputRef = useRef(null);
  const gridRef = useRef(null);
  const resizingCol = useRef(null);

  useEffect(() => {
    if (!sheetId) return;
    sheetsApi.get(sheetId).then(s => {
      setSheet(s);
      setTitle(s.title);
      setActiveSheet(s.active_sheet || 0);
      if (s.sheets?.[0]?.colWidths) setColWidths(s.sheets[0].colWidths);
    }).catch(() => { toast.error('Sheet not found'); navigate('/sheets'); });
  }, [sheetId, navigate]);

  const currentData = sheet?.sheets?.[activeSheet]?.data || {};
  const currentFmt = sheet?.sheets?.[activeSheet]?.formatting || {};

  const cellKey = (r, c) => `${r}_${c}`;
  const getData = useCallback((r, c) => currentData[cellKey(r, c)] || '', [currentData]);

  const getCellDisplay = useCallback((r, c) => {
    const raw = getData(r, c);
    if (typeof raw === 'string' && raw.startsWith('=')) {
      return evalFormula(raw, getData);
    }
    return raw;
  }, [getData]);

  const updateCell = (r, c, value) => {
    const key = cellKey(r, c);
    const newSheets = JSON.parse(JSON.stringify(sheet.sheets || []));
    if (!newSheets[activeSheet].data) newSheets[activeSheet].data = {};
    newSheets[activeSheet].data[key] = value;
    setSheet(prev => ({ ...prev, sheets: newSheets }));
    scheduleAutoSave(newSheets);
  };

  const updateFormatting = (r, c, fmt) => {
    const key = cellKey(r, c);
    const newSheets = JSON.parse(JSON.stringify(sheet.sheets || []));
    if (!newSheets[activeSheet].formatting) newSheets[activeSheet].formatting = {};
    newSheets[activeSheet].formatting[key] = { ...(newSheets[activeSheet].formatting[key] || {}), ...fmt };
    setSheet(prev => ({ ...prev, sheets: newSheets }));
    scheduleAutoSave(newSheets);
  };

  const applyToSelection = (fmt) => {
    if (!selRange && selectedCell) {
      updateFormatting(selectedCell.r, selectedCell.c, fmt);
    } else if (selRange) {
      const newSheets = JSON.parse(JSON.stringify(sheet.sheets || []));
      if (!newSheets[activeSheet].formatting) newSheets[activeSheet].formatting = {};
      for (let r = Math.min(selRange.r1, selRange.r2); r <= Math.max(selRange.r1, selRange.r2); r++) {
        for (let c = Math.min(selRange.c1, selRange.c2); c <= Math.max(selRange.c1, selRange.c2); c++) {
          const key = cellKey(r, c);
          newSheets[activeSheet].formatting[key] = { ...(newSheets[activeSheet].formatting[key] || {}), ...fmt };
        }
      }
      setSheet(prev => ({ ...prev, sheets: newSheets }));
      scheduleAutoSave(newSheets);
    }
  };

  const scheduleAutoSave = (sheets) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try { await sheetsApi.update(sheetId, { sheets, title }); } catch {}
    }, 2000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await sheetsApi.update(sheetId, { sheets: sheet.sheets, title });
      toast.success('Saved!');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const addSheetTab = () => {
    const newSheets = [...(sheet.sheets || [])];
    newSheets.push({ name: `Sheet${newSheets.length + 1}`, data: {}, formatting: {} });
    setSheet(prev => ({ ...prev, sheets: newSheets }));
    setActiveSheet(newSheets.length - 1);
  };

  const deleteSheetTab = (idx) => {
    if ((sheet.sheets || []).length <= 1) return;
    const newSheets = (sheet.sheets || []).filter((_, i) => i !== idx);
    setSheet(prev => ({ ...prev, sheets: newSheets }));
    if (activeSheet >= newSheets.length) setActiveSheet(newSheets.length - 1);
  };

  const startEdit = (r, c) => {
    setEditingCell({ r, c });
    setEditValue(String(getData(r, c)));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitEdit = () => {
    if (editingCell) {
      updateCell(editingCell.r, editingCell.c, editValue);
      setEditingCell(null);
    }
  };

  const handleKeyDown = (e) => {
    if (!selectedCell) return;
    const { r, c } = selectedCell;
    if (e.key === 'Enter') { if (editingCell) commitEdit(); setSelectedCell({ r: Math.min(r + 1, ROWS - 1), c }); }
    else if (e.key === 'Tab') { e.preventDefault(); if (editingCell) commitEdit(); setSelectedCell({ r, c: Math.min(c + 1, COLS - 1) }); }
    else if (e.key === 'Escape') { setEditingCell(null); }
    else if (e.key === 'Delete' || e.key === 'Backspace') {
      if (!editingCell) { updateCell(r, c, ''); }
    }
    else if (e.key === 'ArrowUp' && !editingCell) { e.preventDefault(); setSelectedCell({ r: Math.max(r - 1, 0), c }); }
    else if (e.key === 'ArrowDown' && !editingCell) { e.preventDefault(); setSelectedCell({ r: Math.min(r + 1, ROWS - 1), c }); }
    else if (e.key === 'ArrowLeft' && !editingCell) { e.preventDefault(); setSelectedCell({ r, c: Math.max(c - 1, 0) }); }
    else if (e.key === 'ArrowRight' && !editingCell) { e.preventDefault(); setSelectedCell({ r, c: Math.min(c + 1, COLS - 1) }); }
    else if (e.key === 'F2') { startEdit(r, c); }
    else if (e.ctrlKey || e.metaKey) {
      if (e.key === 'c') { handleCopy(); }
      else if (e.key === 'v') { handlePaste(); }
      else if (e.key === 'x') { handleCut(); }
      else if (e.key === 'z') { e.preventDefault(); }
      else if (e.key === 'b') { e.preventDefault(); applyToSelection({ bold: !currentFmt[cellKey(r, c)]?.bold }); }
      else if (e.key === 'i') { e.preventDefault(); applyToSelection({ italic: !currentFmt[cellKey(r, c)]?.italic }); }
      else if (e.key === 'u') { e.preventDefault(); applyToSelection({ underline: !currentFmt[cellKey(r, c)]?.underline }); }
      else if (e.key === 'f') { e.preventDefault(); setShowFind(true); }
      else if (e.key === 's') { e.preventDefault(); handleSave(); }
    }
    else if (!editingCell && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      startEdit(r, c);
      setEditValue(e.key);
    }
  };

  const handleCopy = () => {
    if (!selectedCell) return;
    const r = selectedCell.r, c = selectedCell.c;
    setClipboard({ value: getData(r, c), fmt: currentFmt[cellKey(r, c)] || {} });
    toast.success('Copied');
  };
  const handleCut = () => {
    handleCopy();
    if (selectedCell) updateCell(selectedCell.r, selectedCell.c, '');
  };
  const handlePaste = () => {
    if (!selectedCell || !clipboard) return;
    updateCell(selectedCell.r, selectedCell.c, clipboard.value);
    if (clipboard.fmt && Object.keys(clipboard.fmt).length) updateFormatting(selectedCell.r, selectedCell.c, clipboard.fmt);
  };

  // Column resize
  const onColResizeStart = (e, ci) => {
    e.preventDefault();
    resizingCol.current = { ci, startX: e.clientX, startW: colWidths[ci] || DEFAULT_COL_W };
    const onMove = (ev) => {
      if (!resizingCol.current) return;
      const diff = ev.clientX - resizingCol.current.startX;
      setColWidths(prev => ({ ...prev, [resizingCol.current.ci]: Math.max(MIN_COL_W, resizingCol.current.startW + diff) }));
    };
    const onUp = () => { resizingCol.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // CSV Export
  const exportCsv = () => {
    const rows = [];
    for (let r = 0; r < ROWS; r++) {
      const row = [];
      let hasData = false;
      for (let c = 0; c < COLS; c++) {
        const v = String(getCellDisplay(r, c) || '');
        row.push(v.includes(',') ? `"${v}"` : v);
        if (v) hasData = true;
      }
      if (hasData) rows.push(row.join(','));
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `${title || 'sheet'}.csv`; a.click();
    toast.success('CSV exported');
  };

  // CSV Import
  const importCsv = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const lines = e.target.result.split('\n');
      const newSheets = JSON.parse(JSON.stringify(sheet.sheets || []));
      if (!newSheets[activeSheet].data) newSheets[activeSheet].data = {};
      lines.forEach((line, ri) => {
        const cols = line.split(',').map(v => v.replace(/^"|"$/g, ''));
        cols.forEach((val, ci) => {
          if (val && ci < COLS) newSheets[activeSheet].data[cellKey(ri, ci)] = val;
        });
      });
      setSheet(prev => ({ ...prev, sheets: newSheets }));
      scheduleAutoSave(newSheets);
      toast.success(`Imported ${lines.length} rows`);
      setShowCsvImport(false);
    };
    reader.readAsText(file);
  };

  // Sort column
  const sortColumn = (ci, asc = true) => {
    const newSheets = JSON.parse(JSON.stringify(sheet.sheets || []));
    const data = newSheets[activeSheet].data || {};
    // Collect rows with data
    const rowData = [];
    for (let r = 0; r < ROWS; r++) {
      const row = {};
      for (let c = 0; c < COLS; c++) {
        const v = data[cellKey(r, c)];
        if (v !== undefined) row[c] = v;
      }
      if (Object.keys(row).length > 0) rowData.push(row);
    }
    rowData.sort((a, b) => {
      const va = a[ci] || '', vb = b[ci] || '';
      const na = parseFloat(va), nb = parseFloat(vb);
      if (!isNaN(na) && !isNaN(nb)) return asc ? na - nb : nb - na;
      return asc ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    // Rebuild data
    newSheets[activeSheet].data = {};
    rowData.forEach((row, r) => {
      Object.entries(row).forEach(([c, v]) => {
        newSheets[activeSheet].data[cellKey(r, parseInt(c))] = v;
      });
    });
    setSheet(prev => ({ ...prev, sheets: newSheets }));
    scheduleAutoSave(newSheets);
    toast.success(asc ? 'Sorted A→Z' : 'Sorted Z→A');
  };

  const selectedCellLabel = selectedCell ? `${COL_LETTERS[selectedCell.c]}${selectedCell.r + 1}` : '';
  const selFmt = selectedCell ? (currentFmt[cellKey(selectedCell.r, selectedCell.c)] || {}) : {};

  if (!sheet) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="h-screen bg-background flex flex-col" tabIndex={0} onKeyDown={handleKeyDown} data-testid="sheet-editor">
      {/* Header */}
      <header className="border-b border-border bg-background shrink-0">
        <div className="px-3 py-1.5 flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/sheets')} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <Input value={title} onChange={e => setTitle(e.target.value)} onBlur={() => sheetsApi.update(sheetId, { title })}
            className="text-base font-semibold border-none shadow-none focus-visible:ring-0 px-1 h-7 w-56" data-testid="sheet-title" />
          <div className="ml-auto flex items-center gap-1.5">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/dashboard')} data-testid="go-dashboard-btn"><LayoutDashboard className="w-3.5 h-3.5 mr-1" />Home</Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={exportCsv} data-testid="export-csv-btn"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowCsvImport(true)} data-testid="import-csv-btn"><Upload className="w-3.5 h-3.5 mr-1" />Import</Button>
            <Button size="sm" className="h-7" onClick={handleSave} disabled={saving} data-testid="save-btn"><Save className="w-3.5 h-3.5 mr-1" />{saving ? '...' : 'Save'}</Button>
          </div>
        </div>

        {/* Toolbar Row 1: Formatting */}
        <div className="px-3 py-1 flex items-center gap-0.5 border-t border-border flex-wrap">
          {/* Cell Reference */}
          <span className="text-[10px] font-mono text-muted-foreground w-10 text-center bg-muted rounded px-1 py-0.5 shrink-0">{selectedCellLabel}</span>
          <div className="w-px h-5 bg-border mx-1" />
          {/* Undo/Redo */}
          <TBtn icon={Undo} title="Undo (Ctrl+Z)" />
          <TBtn icon={Redo} title="Redo (Ctrl+Y)" />
          <div className="w-px h-5 bg-border mx-1" />
          {/* Copy/Cut/Paste */}
          <TBtn icon={Copy} title="Copy (Ctrl+C)" onClick={handleCopy} />
          <TBtn icon={Scissors} title="Cut (Ctrl+X)" onClick={handleCut} />
          <TBtn icon={ClipboardPaste} title="Paste (Ctrl+V)" onClick={handlePaste} />
          <div className="w-px h-5 bg-border mx-1" />
          {/* Font Size */}
          <select value={selFmt.fontSize || fontSize} onChange={e => { setFontSize(e.target.value); applyToSelection({ fontSize: e.target.value }); }}
            className="h-6 text-[10px] bg-background border border-border rounded px-1 w-12" data-testid="font-size">
            {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="w-px h-5 bg-border mx-1" />
          {/* Bold/Italic/Underline/Strikethrough */}
          <TBtn icon={Bold} title="Bold (Ctrl+B)" active={selFmt.bold} onClick={() => applyToSelection({ bold: !selFmt.bold })} tid="fmt-bold" />
          <TBtn icon={Italic} title="Italic (Ctrl+I)" active={selFmt.italic} onClick={() => applyToSelection({ italic: !selFmt.italic })} tid="fmt-italic" />
          <TBtn icon={Underline} title="Underline (Ctrl+U)" active={selFmt.underline} onClick={() => applyToSelection({ underline: !selFmt.underline })} tid="fmt-underline" />
          <TBtn icon={Strikethrough} title="Strikethrough" active={selFmt.strike} onClick={() => applyToSelection({ strike: !selFmt.strike })} tid="fmt-strike" />
          <div className="w-px h-5 bg-border mx-1" />
          {/* Text Color */}
          <div className="relative group">
            <button className="p-1 rounded hover:bg-muted flex items-center" title="Text Color">
              <Type className="w-3.5 h-3.5" /><span className="w-3.5 h-0.5 mt-0.5 block rounded" style={{ backgroundColor: selFmt.color || '#000' }} />
            </button>
            <div className="absolute top-full left-0 mt-1 p-2 bg-card border border-border rounded-lg shadow-xl hidden group-hover:grid grid-cols-4 gap-1 z-50 w-36">
              {TEXT_COLORS.map(c => <button key={c} onClick={() => applyToSelection({ color: c })} className="w-7 h-7 rounded border border-border hover:scale-110 transition-transform" style={{ backgroundColor: c }} />)}
            </div>
          </div>
          {/* Cell Background */}
          <div className="relative group">
            <button className="p-1 rounded hover:bg-muted flex items-center" title="Fill Color">
              <PaintBucket className="w-3.5 h-3.5" /><span className="w-3.5 h-0.5 mt-0.5 block rounded" style={{ backgroundColor: selFmt.bg || 'transparent' }} />
            </button>
            <div className="absolute top-full left-0 mt-1 p-2 bg-card border border-border rounded-lg shadow-xl hidden group-hover:grid grid-cols-3 gap-1 z-50 w-28">
              {CELL_COLORS.map(c => <button key={c} onClick={() => applyToSelection({ bg: c })} className="w-7 h-7 rounded border border-border hover:scale-110 transition-transform" style={{ backgroundColor: c === 'transparent' ? '#fff' : c }} />)}
            </div>
          </div>
          <div className="w-px h-5 bg-border mx-1" />
          {/* Alignment */}
          <TBtn icon={AlignLeft} title="Align Left" active={selFmt.align === 'left'} onClick={() => applyToSelection({ align: 'left' })} />
          <TBtn icon={AlignCenter} title="Align Center" active={selFmt.align === 'center'} onClick={() => applyToSelection({ align: 'center' })} />
          <TBtn icon={AlignRight} title="Align Right" active={selFmt.align === 'right'} onClick={() => applyToSelection({ align: 'right' })} />
          <div className="w-px h-5 bg-border mx-1" />
          {/* Sort */}
          <TBtn icon={SortAsc} title="Sort A→Z" onClick={() => selectedCell && sortColumn(selectedCell.c, true)} />
          <TBtn icon={SortDesc} title="Sort Z→A" onClick={() => selectedCell && sortColumn(selectedCell.c, false)} />
          <TBtn icon={Search} title="Find (Ctrl+F)" onClick={() => setShowFind(!showFind)} />
        </div>

        {/* Formula Bar */}
        <div className="px-3 py-0.5 flex items-center gap-2 border-t border-border bg-muted/30">
          <span className="text-[10px] font-medium text-muted-foreground shrink-0">fx</span>
          <Input value={editingCell ? editValue : (selectedCell ? String(getData(selectedCell.r, selectedCell.c)) : '')}
            onChange={e => { if (editingCell) setEditValue(e.target.value); else if (selectedCell) { startEdit(selectedCell.r, selectedCell.c); setEditValue(e.target.value); } }}
            onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingCell(null); }}
            className="flex-1 h-6 text-xs border-none shadow-none focus-visible:ring-0 bg-transparent" placeholder='Enter value or formula (=SUM(A1:A5), =IF(A1>5,"Yes","No"))' data-testid="formula-bar" />
        </div>

        {/* Find Bar */}
        {showFind && (
          <div className="px-3 py-1 flex items-center gap-2 border-t border-border bg-amber-500/5">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <Input value={findText} onChange={e => setFindText(e.target.value)} placeholder="Find in sheet..."
              className="h-6 text-xs w-48 border-border" autoFocus data-testid="find-input" />
            <span className="text-[10px] text-muted-foreground">
              {findText ? (() => { let c = 0; for (let r = 0; r < ROWS; r++) for (let cc = 0; cc < COLS; cc++) if (String(getData(r, cc)).toLowerCase().includes(findText.toLowerCase())) c++; return `${c} found`; })() : ''}
            </span>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => { setShowFind(false); setFindText(''); }}>Close</Button>
          </div>
        )}
      </header>

      {/* Grid */}
      <div className="flex-1 overflow-auto" ref={gridRef}>
        <table className="border-collapse w-max" data-testid="sheet-grid">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-20 bg-muted w-10 h-6 border border-border text-[10px] text-muted-foreground" />
              {COL_LETTERS.map((col, ci) => (
                <th key={ci} className="sticky top-0 z-10 bg-muted h-6 border border-border text-[10px] text-muted-foreground font-medium px-1 relative select-none"
                  style={{ width: colWidths[ci] || DEFAULT_COL_W, minWidth: MIN_COL_W }}>
                  {col}
                  <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50"
                    onMouseDown={(e) => onColResizeStart(e, ci)} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: ROWS }, (_, ri) => (
              <tr key={ri}>
                <td className="sticky left-0 z-10 bg-muted text-center text-[10px] text-muted-foreground font-medium border border-border w-10 h-6 select-none">{ri + 1}</td>
                {COL_LETTERS.map((_, ci) => {
                  const key = cellKey(ri, ci);
                  const isSelected = selectedCell?.r === ri && selectedCell?.c === ci;
                  const isEditing = editingCell?.r === ri && editingCell?.c === ci;
                  const fmt = currentFmt[key] || {};
                  const isFound = findText && String(getData(ri, ci)).toLowerCase().includes(findText.toLowerCase());
                  return (
                    <td key={ci}
                      onClick={() => { setSelectedCell({ r: ri, c: ci }); if (editingCell) commitEdit(); }}
                      onDoubleClick={() => startEdit(ri, ci)}
                      className={`border border-border px-1 text-xs cursor-cell relative ${isSelected ? 'outline outline-2 outline-primary z-[5]' : ''} ${isFound ? 'bg-amber-200/30' : ''}`}
                      style={{
                        width: colWidths[ci] || DEFAULT_COL_W, minWidth: MIN_COL_W, height: DEFAULT_ROW_H,
                        fontWeight: fmt.bold ? 'bold' : 'normal', fontStyle: fmt.italic ? 'italic' : 'normal',
                        textDecoration: `${fmt.underline ? 'underline' : ''} ${fmt.strike ? 'line-through' : ''}`.trim() || 'none',
                        textAlign: fmt.align || 'left', backgroundColor: fmt.bg || 'transparent',
                        color: fmt.color || 'inherit', fontSize: `${fmt.fontSize || 12}px`,
                      }}
                      data-testid={`cell-${COL_LETTERS[ci]}${ri + 1}`}>
                      {isEditing ? (
                        <input ref={inputRef} value={editValue} onChange={e => setEditValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { commitEdit(); setSelectedCell({ r: ri + 1, c: ci }); } else if (e.key === 'Escape') setEditingCell(null); else if (e.key === 'Tab') { e.preventDefault(); commitEdit(); setSelectedCell({ r: ri, c: Math.min(ci + 1, COLS - 1) }); } }}
                          onBlur={commitEdit}
                          className="w-full h-full border-none outline-none bg-blue-50 dark:bg-blue-900/30 text-xs px-0" autoFocus />
                      ) : (
                        <span className="block truncate">{getCellDisplay(ri, ci)}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sheet Tabs */}
      <div className="border-t border-border bg-card flex items-center gap-0.5 px-2 py-1 shrink-0">
        <button onClick={addSheetTab} className="p-1 text-muted-foreground hover:bg-muted rounded mr-1" data-testid="add-sheet-tab" title="Add Sheet"><Plus className="w-3.5 h-3.5" /></button>
        {(sheet.sheets || []).map((s, i) => (
          <div key={i} className={`group flex items-center gap-1 px-3 py-1 text-xs rounded font-medium transition-colors cursor-pointer ${i === activeSheet ? 'bg-background text-foreground border border-border shadow-sm' : 'text-muted-foreground hover:bg-muted'}`}
            onClick={() => setActiveSheet(i)} data-testid={`sheet-tab-${i}`}>
            {s.name}
            {(sheet.sheets || []).length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); deleteSheetTab(i); }} className="hidden group-hover:block text-red-400 hover:text-red-600"><Trash2 className="w-2.5 h-2.5" /></button>
            )}
          </div>
        ))}
      </div>

      {/* CSV Import Dialog */}
      <Dialog open={showCsvImport} onOpenChange={setShowCsvImport}>
        <DialogContent><DialogHeader><DialogTitle>Import CSV</DialogTitle></DialogHeader>
          <input type="file" accept=".csv" onChange={e => e.target.files[0] && importCsv(e.target.files[0])} data-testid="csv-file-input" />
          <DialogFooter><Button variant="outline" onClick={() => setShowCsvImport(false)}>Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TBtn({ icon: Icon, title, active, onClick, tid }) {
  return (
    <button onClick={onClick} title={title}
      className={`p-1 rounded hover:bg-muted transition-colors ${active ? 'bg-muted text-primary' : 'text-foreground/70'}`}
      data-testid={tid}>
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}
